/**
 * Logistics Submit Policy (capability-aware)
 *
 * Mirrors `invoicingSubmitPolicy`. Reacts to `submit/start` (the
 * AddLogisticsModal "connect" click flips `loading.submitting` false→true and
 * snapshots the connector + credentials into `state.pendingSubmission`) and
 * provisions the selected logistics connector by branching on its declared
 * capabilities via `resolveSetupPanelKind` — NOT a connector-id switch:
 *
 *   - `apiKey`          → POST the credential form to the systems-integrations
 *                         api-key `authorize` route (server validates + stores).
 *   - `directProvision` → call `ProvisionSystemIntegration` directly (no
 *                         credentials needed). Test-logistics.
 *   - anything else     → server-configured connector exposes no setup action;
 *                         the submit is a no-op contract violation.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';
import { LogisticsState } from '../state';
import { LogisticsCommand } from '../commands';
import { ConnectorEntry } from '../../../shared/state/state';
import {
  resolveSetupPanelKind,
  canSubmitApiKey,
  canDirectProvision
} from '../../../shared/state/capabilityGates';

/**
 * Creates a policy that handles logistics-integration submission.
 *
 * @param backend - Client library instance for API calls
 * @param getConnectors - Reads the current connector catalog from shared state
 *   (capability lookup lives in the shared store, not the logistics store).
 */
export const createLogisticsSubmitPolicy = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
): Policy<LogisticsState, LogisticsCommand> => ({
  name: 'logistics-submit',

  // React only when a *submission* starts (pendingSubmission set), so a
  // concurrent `delete/start` — which also flips `loading.submitting` — does
  // not trigger this policy.
  shouldReact: ( prev, next ) =>
    !prev.loading.submitting &&
    next.loading.submitting &&
    next.pendingSubmission !== null,

  onStateChange: async ({ next, ctx }) => {
    const submission = next.pendingSubmission;

    const resetSubmitting = () => {
      ctx.dispatch(
        { type: 'integrations/logistics/submit/error', payload: {}},
        { source: 'api' }
      );
    };

    if ( !submission || !submission.connectorId ) {
      console.error( '[logistics] No connector selected for submission' );
      resetSubmitting();
      return;
    }

    const { connectorId, credentials, label } = submission;
    const connector = getConnectors()[connectorId];
    const panelKind = resolveSetupPanelKind( connector );

    try {
      if ( panelKind === 'apiKey' ) {
        // Defensive capability gate. The modal should not route a non-api-key
        // connector here; if it did we fail loudly rather than mis-submitting.
        if ( !canSubmitApiKey( connector )) {
          throw new Error(
            `Connector ${connectorId} does not declare the api-key capability`
          );
        }

        // Server-side authorization validates the credentials, stores them, and
        // provisions the integration. The FE never provisions an api-key
        // connector directly. The optional display name is forwarded in the
        // authorize body; the connector uses it as the integration `label`.
        await backend.post.systems_integrations_webhooks_post(
          { connectorId, route: 'authorize' },
          { ...credentials, ...( label ? { label } : {}) }
        );

        // Success — close + reset (the close handler clears submitting +
        // pendingSubmission). The newly provisioned integration arrives via the
        // list-load subscription, so no `submit/success` payload is needed here.
        ctx.dispatch(
          { type: 'integrations/logistics/addModal/close', payload: {}},
          { source: 'api' }
        );
        return;
      }

      if ( panelKind === 'directProvision' ) {
        if ( !canDirectProvision( connector )) {
          throw new Error(
            `Connector ${connectorId} does not declare the direct-provision capability`
          );
        }

        // Direct-provision carries NO credentials. `ProvisionSystemIntegration`
        // requires a top-level `integrationUserId` (a fresh per-instance handle).
        // Mirror the proven direct-provision contract: a uuid handle and id
        // `${connectorId}_${integrationUserId}`, no credentials.
        const integrationUserId = uuidv7();
        await backend.commands.ProvisionSystemIntegration({
          id: `${connectorId}_${integrationUserId}`,
          body: {
            connectorId,
            integrationUserId,
            // User-entered display name → integration `label` (distinguishes
            // multiple instances of the same connector). Omitted when blank.
            ...( label ? { label } : {})
          } as any
        });

        // Success — close + reset (the close handler clears submitting +
        // pendingSubmission). The newly provisioned integration arrives via the
        // list-load subscription, so no `submit/success` payload is needed here.
        ctx.dispatch(
          { type: 'integrations/logistics/addModal/close', payload: {}},
          { source: 'api' }
        );
        return;
      }

      // `oauth` / `serverConfigured` — no logistics setup action exists.
      console.warn(
        `[logistics] Submit attempted on connector ${connectorId} with unsupported panel kind '${panelKind}'; ignoring.`
      );
      resetSubmitting();
    } catch ( error ) {
      console.error( '[logistics] Submit failed:', error );
      resetSubmitting();
      throw error;
    }
  }
});
